import { faDiamond } from "@fortawesome/free-solid-svg-icons/faDiamond";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons/faEllipsis";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons/faPenToSquare";
import { faRotate } from "@fortawesome/free-solid-svg-icons/faRotate";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons/faRotateLeft";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons/faTrashCan";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons/faWhatsapp";
import type { ColumnType, MenuProps } from "../../../utils/local-antd";

import { TFunction } from "i18next";
import isFunction from "lodash/isFunction";
import styled from "styled-components";

import { AssigneeBanner } from "../../../components/AssigneeBanner";
import { AtiraDropdown } from "../../../components/AtiraDropdown";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { EntriesTableSettings } from "../../../model/entries-table-settings/EntriesTableSettings";
import { Entry } from "../../../model/entry/Entry";
import { EntryKind } from "../../../model/entry/types/EntryKind.enum";
import { Form } from "../../../model/form/Form";
import { Permission } from "../../../model/permissions/Permission";
import { User } from "../../../model/user/User";
import { Spacing } from "../../../theme/Spacing";
import { standardDate } from "../../../utils/Date";
import { CustomFieldEditableCell } from "../components/CustomFieldEditableCell";
import { getTableCellClassName } from "./getTableCellClassName";
import { ModuleDefinition } from "../../../model/module/types/module-definition/ModuleDefinition";
import { faCubes } from "@fortawesome/free-solid-svg-icons";
import { UserRoles } from "../../../model/user/types/UserRoles";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { TableActionableCellEmail } from "../../../components/table/TableActionableCellEmail";
import { TableActionableCellPhone } from "../../../components/table/TableActionableCellPhone";
import { ModuleDefinitionKind } from "../../../model/module/types/module-definition/ModuleDefinitionKind";
import { faLink } from "@fortawesome/free-solid-svg-icons/faLink";
import { EntityIcons } from "../../../utils/GlobalIcons";
import { TableActionableCellPreview } from "../../../components/table/TableActionableCellPreview";
import { TableActionableLink } from "../../../components/table/TableActionableCellLink";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons/faCircleNotch";
import { faTags } from "@fortawesome/free-solid-svg-icons/faTags";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { TestIDs } from "../../../tests/types/testIds";
import { Tag } from "../../../model/tag/Tag";
import { useAppSelector } from "../../../redux/store";
import { tagSliceSelectors } from "../../../redux/tag/tag.selector";
import { TagDot } from "../../../components/TagChip";
import { TagList } from "../../../components/TagList";
import { EntryTagsMenuPopup } from "../components/EntryTagsMenuPopup";

const ActionContainer = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
`;

const TaskButton = styled(Button)`
  color: ${({ theme }) => theme.black};
  padding: ${Spacing.xs} ${Spacing.m};
  margin: 0;
  border: ${({ theme }) => `1px solid ${theme.black}`};
  background-color: ${({ theme }) => theme.white};
  width: 100%;
`;

const TagCheck = styled(Flex)`
  margin-inline-start: auto;
`;

type FNType = ATVoidFunction<Entry>;

type FieldFunctions = {
  delete: FNType;
  convert: FNType;
  details: FNType;
  deal: FNType;
  update: FNType;
  updateForCustomField: (entry: Partial<Entry>) => Promise<boolean>;
  createContactTask: FNType;
  createLeadTask: FNType;
  readTasks: FNType;
  assign?: FNType;
  retrieve?: FNType;
  startWhatsappChat?: FNType;
  toggleTag?: (entry: Entry, tagId: string) => Promise<void>;
  manageTags?: VoidFunction;
  createModuleInstance?: ATVoidFunction<{
    entry: Entry;
    module: ModuleDefinition;
  }>;
  attachModuleInstance?: ATVoidFunction<{
    entry: Entry;
    module: ModuleDefinition;
  }>;
  t: TFunction;
};

const getFormTitle = (t: TFunction, form?: Form) => {
  if (form?.title) {
    return (
      <TextWithTruncated length={25}>
        {form.orphan ? t("forms.manual_entry") : form.title}
      </TextWithTruncated>
    );
  }

  return t("entries.table.entry.unknown_title");
};

const getFieldWidth = (field: string) => {
  switch (true) {
    case ["actions", "assignee"].includes(field):
      return 100;
    case field === "task":
      return 140;
    case field === "phone" || field.startsWith("custom-field-"):
      return 250;
    case field === "email" || field === "description":
      return 375;
    default:
      return 200;
  }
};

const getActionMenuItems = (props: {
  entry: Entry;
  t: TFunction;
  permissions: Permission["permissions"];
  functions: FieldFunctions;
  user?: User;
  fixedModules?: ModuleDefinition[];
  dynamicModules?: ModuleDefinition[];
  tags: Tag[];
  tagSearch: string;
  setTagSearch: (value: string) => void;
  onTagClick: (tagId: string) => void;
  pendingTagId: string | null;
}): MenuProps["items"] => {
  const {
    functions: {
      delete: deleteFn,
      convert,
      details,
      update,
      deal,
      createContactTask,
      createLeadTask,
      assign,
      retrieve,
      startWhatsappChat,
      createModuleInstance,
      attachModuleInstance,
      toggleTag,
      manageTags,
    },
    user,
    t,
    entry,
    permissions,
    fixedModules,
    dynamicModules,
    tags,
    tagSearch,
    setTagSearch,
    onTagClick,
    pendingTagId,
  } = props;

  const tagQuery = tagSearch.trim().toLowerCase();
  const matchingTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(tagQuery),
  );

  return [
    {
      key: "table-g1",
      type: "group",
      children: [
        {
          label: (
            <ActionContainer
              dataTestId={TestIDs.entries.table.menu.edit.button(entry._id!)}
            >
              <AtiraIcon icon={faPenToSquare} color="main" />
              <Text color="main">{t("entries.table.entry.edit")}</Text>
            </ActionContainer>
          ),
          onClick: () => update(entry),
          valid: permissions.ENTRY.UPDATE && !Boolean(entry.markedForDeletion),
        },
        {
          label: (
            <ActionContainer
              dataTestId={TestIDs.entries.table.menu.restore.button(entry._id!)}
            >
              <AtiraIcon icon={faRotateLeft} color="main" />
              <Text color="main">{t("common.retrieve")}</Text>
            </ActionContainer>
          ),
          onClick: () => retrieve?.(entry),
          valid:
            isFunction(retrieve) &&
            permissions.ENTRY.READ &&
            permissions.ENTRY.DELETE &&
            Boolean(entry.markedForDeletion),
        },
        {
          label: (
            <ActionContainer>
              <AtiraIcon icon={faEye} color="main" />
              <Text color="main">{t("common.preview")}</Text>
            </ActionContainer>
          ),
          onClick: () => details(entry),
        },
        {
          label: (
            <ActionContainer
              dataTestId={TestIDs.entries.table.menu.whatsapp.button(
                entry._id!,
              )}
            >
              <AtiraIcon icon={faWhatsapp} color="main" />
              <Text color="main">
                {t("entries.table.entry.whatsapp.start")}
              </Text>
            </ActionContainer>
          ),
          onClick: () => startWhatsappChat?.(entry),
          valid:
            isFunction(startWhatsappChat) &&
            Boolean(entry.fields?.phone) &&
            !Boolean(entry.markedForDeletion),
        },
        {
          label: (
            <ActionContainer
              dataTestId={TestIDs.entries.table.menu.deal.button(entry._id!)}
            >
              <AtiraIcon icon={faDiamond} color="main" />
              <Text color="main">{t("common.deal")}</Text>
            </ActionContainer>
          ),
          onClick: () => deal(entry),
          valid:
            [EntryKind.CONTACT, EntryKind.LEAD].includes(entry.kind) &&
            permissions.KANBAN_CARD.CREATE &&
            !Boolean(entry.markedForDeletion),
        },
        {
          label: (
            <ActionContainer>
              <AtiraIcon icon={faCubes} color="main" />
              <Text color="main">{t("common.modules")}</Text>
            </ActionContainer>
          ),
          key: "modules",
          children: [
            ...(fixedModules?.length
              ? [
                  {
                    type: "group",
                    label: t("entries.table.modules.fixed.title"),
                    children: fixedModules.map((module) => ({
                      key: module._id,
                      label: (
                        <ActionContainer>
                          <AtiraIcon icon={faLink} color="main" />
                          <TextWithTruncated length={Lengths.NAME} color="main">
                            {module.name}
                          </TextWithTruncated>
                        </ActionContainer>
                      ),
                      onClick: () => attachModuleInstance?.({ entry, module }),
                    })),
                  },
                  {
                    type: "divider",
                    key: "divider-1",
                  },
                ]
              : []),
            ...(dynamicModules?.length
              ? [
                  {
                    type: "group",
                    label: t("entries.table.modules.dynamic.title"),
                    children: dynamicModules.map((module) => ({
                      key: module._id,
                      label: (
                        <ActionContainer>
                          <AtiraIcon icon={faPlus} color="main" />
                          <TextWithTruncated length={Lengths.NAME} color="main">
                            {module.name}
                          </TextWithTruncated>
                        </ActionContainer>
                      ),
                      onClick: () => createModuleInstance?.({ entry, module }),
                    })),
                  },
                ]
              : []),
          ],
          valid:
            entry.kind !== EntryKind.JUNK &&
            Boolean(dynamicModules?.length || fixedModules?.length) &&
            !user?.roles?.includes(UserRoles.MEMBER) &&
            (isFunction(createModuleInstance) ||
              isFunction(attachModuleInstance)),
        },
        {
          label: (
            <ActionContainer
              dataTestId={TestIDs.entries.table.menu.assign.button(entry._id!)}
            >
              <AtiraIcon icon={faPenToSquare} color="main" />
              <Text color="main">{t("common.assign")}</Text>
            </ActionContainer>
          ),
          onClick: () => assign?.(entry),
          valid: isFunction(assign) && !Boolean(entry.markedForDeletion),
        },
        {
          label: (
            <ActionContainer
              dataTestId={TestIDs.entries.table.menu.tags.button(entry._id!)}
            >
              <AtiraIcon icon={faTags} color="main" />
              <Text color="main">{t("common.tags")}</Text>
            </ActionContainer>
          ),
          key: "tags",
          children: [
            ...(matchingTags.length
              ? matchingTags.map((tag) => ({
                  key: `tag-${tag._id}`,
                  disabled: Boolean(pendingTagId),
                  label: (
                    <ActionContainer
                      dataTestId={TestIDs.entries.table.menu.tags.option(
                        tag.name,
                      )}
                    >
                      <TagDot $color={tag.color} />
                      <TextWithTruncated length={Lengths.NAME} color="main">
                        {tag.name}
                      </TextWithTruncated>
                      {pendingTagId === tag._id ? (
                        <TagCheck>
                          <AtiraIcon
                            icon={faCircleNotch}
                            color="main"
                            animations={["spin"]}
                          />
                        </TagCheck>
                      ) : entry.tagIds?.includes(tag._id) ? (
                        <TagCheck>
                          <AtiraIcon icon={faCheck} color="main" />
                        </TagCheck>
                      ) : null}
                    </ActionContainer>
                  ),
                  onClick: () => onTagClick(tag._id),
                }))
              : [
                  {
                    key: "tags-empty",
                    disabled: true,
                    label: (
                      <Text fontSize="s" color="darkerSub">
                        {t(
                          tags.length
                            ? "entries.table.entry.tags.noMatch"
                            : "entries.table.entry.tags.empty",
                        )}
                      </Text>
                    ),
                  },
                ]),
            ...(isFunction(manageTags)
              ? [
                  { type: "divider" as const, key: "tags-divider" },
                  {
                    key: "tags-manage",
                    label: (
                      <ActionContainer
                        dataTestId={TestIDs.entries.table.menu.tags.manage}
                      >
                        <AtiraIcon icon={faGear} color="main" />
                        <Text color="main">
                          {t("entries.table.entry.tags.manage")}
                        </Text>
                      </ActionContainer>
                    ),
                    onClick: manageTags,
                  },
                ]
              : []),
          ],
          popupRender: (menu: React.ReactNode) => (
            <EntryTagsMenuPopup
              menu={menu}
              search={tagSearch}
              onSearch={setTagSearch}
              searchTestId={TestIDs.entries.table.menu.tags.search(entry._id)}
            />
          ),
          valid:
            isFunction(toggleTag) &&
            permissions.ENTRY.UPDATE &&
            !Boolean(entry.markedForDeletion),
        },
        {
          label: (
            <ActionContainer
              dataTestId={TestIDs.entries.table.menu.convert.button(entry._id!)}
            >
              <AtiraIcon icon={faRotate} color="main" />
              <Text color="main">{t("common.convert")}</Text>
            </ActionContainer>
          ),
          onClick: () => convert(entry),
          valid: permissions.ENTRY.UPDATE && !Boolean(entry.markedForDeletion),
        },
        {
          label: (
            <ActionContainer
              dataTestId={TestIDs.entries.table.menu.taskContact.button(
                entry._id!,
              )}
            >
              <AtiraIcon icon={EntityIcons.Tasks} color="main" />
              <Text color="main">{t("tasks.contact")}</Text>
            </ActionContainer>
          ),
          onClick: () => createContactTask(entry),
          valid:
            entry.kind === EntryKind.CONTACT &&
            (!entry.contactTasks?.length || entry.contactTasks.length < 3) &&
            permissions.CONTACT_TASK.CREATE &&
            !Boolean(entry.markedForDeletion),
        },
        {
          label: (
            <ActionContainer>
              <AtiraIcon icon={EntityIcons.Tasks} color="main" />
              <Text color="main">{t("tasks.lead")}</Text>
            </ActionContainer>
          ),
          onClick: () => createLeadTask(entry),
          valid:
            entry.kind === EntryKind.LEAD &&
            (!entry.leadTasks?.length || entry.leadTasks.length < 3) &&
            permissions.LEAD_TASK.CREATE &&
            !Boolean(entry.markedForDeletion),
        },
        {
          label: (
            <ActionContainer
              dataTestId={TestIDs.entries.table.menu.delete.button(entry._id!)}
            >
              <AtiraIcon icon={faTrashCan} color="main" />
              <Text color="main">{t("common.delete")}</Text>
            </ActionContainer>
          ),
          onClick: () => deleteFn(entry),
          valid:
            permissions.ENTRY.DELETE &&
            (entry.markedForDeletion
              ? !user?.roles?.includes(UserRoles.MEMBER)
              : true),
        },
      ]
        .filter((item) => item.valid !== false)
        .map((child, index) => ({
          ...child,
          // A submenu keeps its own key: items before it come and go as data
          // loads, and a shifted key remounts the submenu closed
          key: child.key ?? index.toString(),
          style: { padding: 4 },
        })),
    },
  ];
};

const createEmailSorter = (a: Entry, b: Entry) =>
  a.fields?.email?.localeCompare(b.fields?.email || "") || 0;

const createNameSorter = (a: Entry, b: Entry) =>
  a.fields?.name?.localeCompare(b.fields?.name || "") || 0;

const createDateSorter = (a: Entry, b: Entry) => {
  const dateA = new Date(a.createdAt).getTime();
  const dateB = new Date(b.createdAt).getTime();
  return dateA - dateB;
};

type ActionsDropdownProps = {
  entry: Entry;
  t: TFunction;
  permissions: Permission["permissions"];
  functions: FieldFunctions;
  user?: User;
  fixedModules?: ModuleDefinition[];
  dynamicModules?: ModuleDefinition[];
};

const ActionsDropdown: React.FC<ActionsDropdownProps> = memo(
  ({
    entry,
    t,
    permissions,
    functions,
    user,
    fixedModules,
    dynamicModules,
  }) => {
    const [open, setOpen] = useState(false);
    const [tagSearch, setTagSearch] = useState("");
    const [pendingTagId, setPendingTagId] = useState<string | null>(null);
    const keepOpenRef = useRef(false);

    const tags = useAppSelector(tagSliceSelectors.selectTags);

    const onTagClick = useCallback(
      async (tagId: string) => {
        keepOpenRef.current = true;

        if (pendingTagId) {
          return;
        }

        setPendingTagId(tagId);

        try {
          await functions.toggleTag?.(entry, tagId);
        } finally {
          setPendingTagId(null);
        }
      },
      [entry, functions, pendingTagId],
    );

    // Clicking any item closes the menu, but several tags are often picked in
    // a row, so a tag click keeps it open
    const onOpenChange = useCallback(
      (next: boolean, info: { source: "trigger" | "menu" }) => {
        const keepOpen = keepOpenRef.current;
        keepOpenRef.current = false;

        if (!next && info.source === "menu" && keepOpen) {
          return;
        }

        setOpen(next);

        if (!next) {
          setTagSearch("");
        }
      },
      [],
    );

    const items = useMemo(
      () =>
        getActionMenuItems({
          entry,
          functions,
          permissions,
          user,
          fixedModules,
          dynamicModules,
          t,
          tags,
          tagSearch,
          setTagSearch,
          onTagClick,
          pendingTagId,
        }),
      [
        entry,
        functions,
        permissions,
        user,
        fixedModules,
        dynamicModules,
        t,
        tags,
        tagSearch,
        onTagClick,
        pendingTagId,
      ],
    );

    return (
      <AtiraDropdown
        items={items}
        placement="bottomLeft"
        trigger={["click"]}
        open={open}
        onOpenChange={onOpenChange}
        // Otherwise a click collapses the open tags submenu, even though the
        // menu itself is kept open for the next tag
        menu={{ multiple: true }}
        destroyOnHidden
        dataTestId={TestIDs.entries.table.actionsCell(entry._id!)}
      >
        <Button
          icon={faEllipsis}
          margin="none"
          color="black"
          backgroundColor="white"
        />
      </AtiraDropdown>
    );
  },
);

const FieldRenderer = (
  column: EntriesTableSettings["columns"][number],
  functions: FieldFunctions,
  data: {
    permissions: Permission["permissions"];
    members?: User[];
    user?: User;
    fixedModules?: ModuleDefinition[];
    dynamicModules?: ModuleDefinition[];
  },
): ColumnType<Entry> & { data: EntriesTableSettings["columns"][number] } => {
  const { field, label } = column;
  const { permissions, members, user, fixedModules, dynamicModules } = data;
  const { t, details, updateForCustomField, readTasks } = functions;

  switch (true) {
    case field === "actions":
      return {
        title: t("common.actions"),
        key: "table-actions",
        render: (entry: Entry) => {
          return {
            children: (
              <Flex justifyContent="center">
                <ActionsDropdown
                  entry={entry}
                  t={t}
                  permissions={permissions}
                  functions={functions}
                  user={user}
                  fixedModules={fixedModules}
                  dynamicModules={dynamicModules}
                />
              </Flex>
            ),
          };
        },
        onCell: () => ({
          style: { minWidth: getFieldWidth(field) },
        }),
        fixed: "left",
        data: column,
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
      };

    case field === "task":
      return {
        title: t("common.task"),
        key: "table-task",
        render: (entry: Entry) => {
          // const priorityOrder = ["high", "medium", "low"];
          // const priorities =
          //   entry?.contactTasks?.map((t) => t.priority?.toLowerCase()) || [];
          // const highestPriority =
          //   priorityOrder.find((p) => priorities.includes(p)) || "";
          return {
            props: {
              // className: `${highestPriority}-priority-task`,
            },
            children:
              permissions.LEAD_TASK.READ || permissions.CONTACT_TASK.READ ? (
                <TaskButton icon={faCheck} onClick={() => readTasks(entry)}>
                  {t("common.tasks")}
                </TaskButton>
              ) : (
                <></>
              ),
          };
        },
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
        fixed: "left",
        data: column,
      };

    case field === "assignee":
      return {
        title: t("common.assignees"),
        key: "assignee",
        render: (entry: Entry) => (
          <AssigneeBanner
            assignees={members?.filter((m) =>
              entry?.assigneeIds?.includes(m._id),
            )}
          />
        ),
        data: column,
        fixed: "left",
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
        ellipsis: true,
      };

    case field === "tags":
      return {
        title: t("common.tags"),
        key: "table-tags",
        render: (entry: Entry) => (
          <Flex dataTestId={TestIDs.entries.table.tagsCell(entry._id)}>
            <TagList tagIds={entry.tagIds} max={3} />
          </Flex>
        ),
        data: column,
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
      };

    case field === "name":
      return {
        title: t("common.name"),
        key: "table-name",
        render: (entry: Entry) => {
          return {
            props: { className: getTableCellClassName(entry.kind) },
            children: (
              <TableActionableCellPreview
                text={entry.fields?.name}
                onPreview={() => details(entry)}
              />
            ),
          };
        },
        sorter: createNameSorter,
        data: column,
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
      };

    case field === "source":
      return {
        title: t("common.source"),
        key: "table-source",
        render: (entry: Entry) => {
          return {
            props: { className: getTableCellClassName(entry.kind) },
            children: (
              <TableActionableLink
                link={entry.origin}
                extra={{ kind: entry.kind }}
              />
            ),
          };
        },
        data: column,
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
      };

    /** time is the document's created_at field */
    case field === "created":
      return {
        title: t("entries.table.header.submit_time"),
        key: "table-time",
        render: ({ createdAt }: Entry) => (
          <Text>{standardDate<string>(createdAt, user, true)}</Text>
        ),
        sorter: createDateSorter,
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
        data: column,
      };

    case field === "form":
      return {
        title: t("entries.table.header.form_title"),
        key: "table-formTitle",
        render: ({ parentForm }: Entry) =>
          permissions.FORM.READ ? getFormTitle(t, parentForm) : "",
        data: column,
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
      };

    case field === "phone":
      return {
        title: t("common.phone"),
        key: "table-phone",
        render: ({ fields, kind }: Entry) => (
          <TableActionableCellPhone phone={fields?.phone} extra={{ kind }} />
        ),
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
        data: column,
      };

    case field === "email":
      return {
        title: t("common.email"),
        key: "table-email",
        render: ({ fields, kind }: Entry) => {
          return {
            props: { className: getTableCellClassName(kind) },
            children: fields?.email ? (
              <TableActionableCellEmail email={fields.email} extra={{ kind }} />
            ) : null,
          };
        },
        sorter: createEmailSorter,
        data: column,
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
      };

    case field.startsWith("custom-field"):
      return {
        title: label,
        key: `table-${field}`,
        render: (entry: Entry) => {
          const inputs = entry.parentForm?.inputs || [];
          const input = inputs.find((input) => input.name === field);

          return {
            children: (
              <CustomFieldEditableCell
                entry={entry}
                field={field}
                type={input?.type}
                dateInputConfig={input?.dateInputConfig}
                onUpdate={updateForCustomField}
                disableCountrySelect
              />
            ),
            props: { className: "custom-field-cell" },
          };
        },
        data: column,
        ellipsis: true,
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
      };

    default:
      return {
        title: t(`common.${field}`),
        key: `table-${field}`,
        render: (entry: Entry) => {
          const value =
            (
              entry[field as keyof Entry] ||
              entry.fields?.[field as keyof Entry["fields"]]
            )?.toString() || "";

          return <TextWithTruncated length={25}>{value}</TextWithTruncated>;
        },
        data: column,
        ellipsis: true,
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
      };
  }
};

export function createEntriesTableColumns<T>(
  columns: EntriesTableSettings["columns"],
  functions: FieldFunctions,
  data: {
    permissions: Permission["permissions"];
    user: User;
    members?: User[];
    moduleDefinitions?: ModuleDefinition[];
  },
): ColumnType<T>[] {
  const fixedModules =
    data.moduleDefinitions?.filter(
      (m) => m.kind === ModuleDefinitionKind.FIXED,
    ) || [];
  const dynamicModules =
    data.moduleDefinitions?.filter(
      (m) => m.kind === ModuleDefinitionKind.DYNAMIC,
    ) || [];

  return columns.map((c) =>
    FieldRenderer(c, functions, { fixedModules, dynamicModules, ...data }),
  ) as ColumnType<T>[];
}
